import { Dispatcher } from "./api/dispatcher"
import {
  FETCH_ERROR_STATUS,
  getNetworkFailureUserMessage,
  isDeviceOffline,
  isNetworkError,
} from "./api/utils"
import { HTTP_STATUS } from "./constants"

import { appendLangToUrl } from "./redux/api/fetchBaseQueryExtended"
import { FEATURE } from "./redux/payment/paymentSlice"

const dispatcher = Dispatcher.getInstance()

/**
 * JOAN-10552: thin wrapper around `fetch`. Handles auth/payment side
 * effects globally so legacy callers in `src/api/fetch.ts` (postJSON,
 * putJSON, etc.) propagate consistent state changes on 401/402. No
 * automatic retries — callers see the first response (success or error)
 * and decide what to do.
 *
 * JOAN-11550: logo upload and other legacy thunks toast `error.message`
 * directly. Map online fetch failures to a generic failure message so users
 * never see "Failed to fetch (host)".
 */
export async function fetchExtended(
  url: RequestInfo,
  settings: RequestInit,
): Promise<Response> {
  url = appendLangToUrl(url.toString())

  let response: Response

  try {
    response = await fetch(url, settings)
  } catch (error) {
    if (isNetworkError(error) && !isDeviceOffline()) {
      const rawMessage = error instanceof Error ? error.message : String(error)

      /**
       * The translated message no longer matches `isNetworkError`, so carry
       * RTK's connectivity status along for the offline overlay and Sentry.
       */
      throw Object.assign(new Error(getNetworkFailureUserMessage(rawMessage)), {
        code: FETCH_ERROR_STATUS,
      })
    }

    throw error
  }

  // If 2xx code, continue
  if (response.ok) {
    return response
  }

  if (response.status === HTTP_STATUS.UNAUTHORIZED) {
    dispatcher.dispatch("clearAuth")

    return response
  }

  if (response.status === HTTP_STATUS.PAYMENT_REQUIRED) {
    const lockedFeature = detectLockedFeature(url)
    dispatcher.dispatch("setHasPaid", lockedFeature)
  }

  return response
}

// Parts of URLs for detecting which feature is locked
const DESK_BOOKING_LOCK_URLS = [
  /desk\/company\/[\w-]+\/reservation/,
  /v2\/me\/reservation/,
  /reservation\/[/\w-]+\//,
  /portal\/desks\/schedule\//,
  /portal\/people\/schedule\//,
  /portal\/desks\/reservations\//,
]

const ASSET_BOOKING_LOCK_URLS = [
  "/assets/schedule/",
  "/portal/assets/reservations",
]

const VISITOR_LOCK_URLS = ["portal/visitors/"]

const ROOM_BOOKING_LOCK_URLS = ["portal/rooms/"]

// When the API returns 402, we infer which feature to mark as locked from the request URL.
export const detectLockedFeature = (url: RequestInfo) => {
  const urlStr = url.toString()

  if (
    ASSET_BOOKING_LOCK_URLS.reduce(
      (isLocked, path) => urlStr.includes(path) || isLocked,
      false,
    )
  ) {
    return FEATURE.desk
  }

  if (
    DESK_BOOKING_LOCK_URLS.reduce(
      (isLocked, pathRegEx) => pathRegEx.test(urlStr) || isLocked,
      false,
    )
  ) {
    return FEATURE.desk
  }

  if (
    VISITOR_LOCK_URLS.reduce(
      (isLocked, path) => urlStr.includes(path) || isLocked,
      false,
    )
  ) {
    return FEATURE.visitor
  }

  if (
    ROOM_BOOKING_LOCK_URLS.reduce(
      (isLocked, path) => urlStr.includes(path) || isLocked,
      false,
    )
  ) {
    return FEATURE.room
  }

  return null
}
